/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'

import colors from '@sportninja/common/constants/appColors'
import type {
  GameStatusInfo,
  GameStatusTone,
} from '@sportninja/common/features/game/gameListView/gameStatus'

import { font, weightSemiBold } from 'src/components/css'
import { gameStatusFullLabel, gameStatusLabel } from '../utils/gameStatusLabel'
import { gameViewsLabel } from '../utils/i18n'

export interface GameStatusTagProps {
  status: GameStatusInfo
  /**
   * `grid` is the Details cell; `card` is the calendar's, which lives inside a
   * 110px month cell and has to give up the padding to fit.
   */
  size?: 'grid' | 'card'
}

/**
 * One status, one look, both views.
 *
 * Deliberately a TINT plus a text label rather than a colour alone: "Postponed"
 * and "Cancelled" are the two an admin most needs to catch, and a reader who
 * cannot separate amber from red — or a screenshot pasted into a ticket — gets
 * nothing from a bare dot.
 */
const GameStatusTag = ({ status, size = 'grid' }: GameStatusTagProps) => {
  'use memo'

  const label = gameStatusLabel(status)
  const full = gameStatusFullLabel(status)

  return (
    <span
      css={[tagStyles(status.tone), size === 'card' && cardStyles]}
      title={full}
      data-testid={`game-status-${status.key}`}
      data-status-tone={status.tone}
    >
      {label}
      {status.inReview && (
        <span css={reviewStyles} data-testid='game-status-in-review'>
          {gameViewsLabel('gameStatusInReview')}
        </span>
      )}
    </span>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

/**
 * Tone → colour. Each is the tint over the row's own surface plus a readable
 * foreground, in the house palette — no new hexes.
 */
const TONE_COLORS: Record<GameStatusTone, { fg: string; bg: string }> = {
  neutral: { fg: colors.NEUTRAL_50, bg: `${colors.NEUTRAL_50}1A` },
  live: { fg: colors.SUCCESS_100, bg: `${colors.SUCCESS_300}26` },
  done: { fg: colors.INFO_100, bg: `${colors.INFO_300}26` },
  warning: { fg: colors.WARNING_200, bg: `${colors.WARNING_300}26` },
  danger: { fg: colors.ERROR_200, bg: `${colors.ERROR_300}26` },
}

const tagStyles = (tone: GameStatusTone) => css`
  ${font.body}
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${TONE_COLORS[tone].fg};
  background-color: ${TONE_COLORS[tone].bg};
`

const cardStyles = css`
  padding: 0 4px;
  font-size: 10px;
  line-height: 14px;
  gap: 4px;
`

const reviewStyles = css`
  color: ${colors.NEUTRAL_50};
  font-size: 0.9em;
  opacity: 0.9;
`

export default GameStatusTag
